// components/Contact.jsx — Contact form with EmailJS integration

import { useState } from "react";
import { SectionWrapper, SectionTitle, Input, Textarea, Button, Label } from "./UI";
import { whatsappLink } from "../utils/helpers";
import emailjs from "@emailjs/browser";

const EMAILJS_SERVICE_ID  = "service_xf2z3cc";
const EMAILJS_TEMPLATE_ID = "template_4zhjy4x";
const EMAILJS_PUBLIC_KEY  = "3DZRFO6m6HPmRI67i";

export default function Contact({ content, t, dark, hasBg = false, lang }) {
  const [form, setForm]     = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState(null);
  const isEs = lang === "es";

  const title    = isEs ? (content.titleEs    || content.title)    : content.title;
  const subtitle = isEs ? (content.subtitleEs || content.subtitle) : content.subtitle;
  const namePh   = isEs ? (content.namePlaceholderEs    || content.namePlaceholder)    : content.namePlaceholder;
  const emailPh  = isEs ? (content.emailPlaceholderEs   || content.emailPlaceholder)   : content.emailPlaceholder;
  const msgPh    = isEs ? (content.messagePlaceholderEs || content.messagePlaceholder) : content.messagePlaceholder;
  const sendBtn  = isEs ? (content.sendButtonTextEs     || content.sendButtonText)     : content.sendButtonText;

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
        name: form.name, email: form.email, message: form.message,
      }, EMAILJS_PUBLIC_KEY);
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      console.error("EmailJS error:", err);
      setStatus("error");
    }
  };

  return (
    <SectionWrapper id="contact" dark={dark} bg={hasBg ? "alt" : "default"}>
      <SectionTitle title={title} subtitle={subtitle} dark={dark} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label dark={dark}>{isEs ? "Nombre" : "Name"}</Label>
            <Input dark={dark} name="name" value={form.name} onChange={handleChange} placeholder={namePh} required />
          </div>
          <div>
            <Label dark={dark}>{isEs ? "Correo electrónico" : "Email"}</Label>
            <Input dark={dark} name="email" type="email" value={form.email} onChange={handleChange} placeholder={emailPh} required />
          </div>
          <div>
            <Label dark={dark}>{isEs ? "Mensaje" : "Message"}</Label>
            <Textarea dark={dark} name="message" value={form.message} onChange={handleChange} placeholder={msgPh} rows={5} required />
          </div>

          {status === "sent" ? (
            <div className="rounded-xl bg-[#A3E635]/10 border border-[#A3E635]/30 text-[#A3E635] px-5 py-3.5 text-sm font-medium">
              ✓ {isEs ? "¡Mensaje enviado! Te responderemos pronto." : "Message sent! We'll get back to you shortly."}
            </div>
          ) : status === "error" ? (
            <div className="rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 px-5 py-3.5 text-sm font-medium">
              ✗ {isEs ? "Algo salió mal. Por favor inténtalo de nuevo." : "Something went wrong. Please try again."}
            </div>
          ) : (
            <Button type="submit" size="lg" className="w-full" disabled={status === "sending"}>
              {status === "sending" ? (isEs ? "Enviando…" : "Sending…") : sendBtn}
            </Button>
          )}
        </form>

        <div className="space-y-6">
          <p className={`text-sm font-semibold uppercase tracking-wider ${dark ? "text-white/40" : "text-[#0F172A]/40"}`}>
            {t.contact.orReach}
          </p>

          <a href={`mailto:${content.email}`} className="flex items-center gap-4 group">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-colors ${dark ? "bg-white/5 group-hover:bg-[#A3E635]/10" : "bg-[#E2E8F0]/70 group-hover:bg-[#A3E635]/10"}`}>📧</div>
            <div>
              <p className={`text-xs mb-0.5 ${dark ? "text-white/40" : "text-[#0F172A]/40"}`}>Email</p>
              <p className="text-sm font-semibold group-hover:text-[#A3E635] transition-colors">{content.email}</p>
            </div>
          </a>

          <a href={whatsappLink(content.whatsapp, isEs ? "¡Hola! Estoy interesado en tus servicios de diseño web." : "Hi! I'm interested in your web design services.")} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-colors ${dark ? "bg-white/5 group-hover:bg-[#A3E635]/10" : "bg-[#E2E8F0]/70 group-hover:bg-[#A3E635]/10"}`}>💬</div>
            <div>
              <p className={`text-xs mb-0.5 ${dark ? "text-white/40" : "text-[#0F172A]/40"}`}>{t.contact.whatsapp}</p>
              <p className="text-sm font-semibold group-hover:text-[#A3E635] transition-colors">{content.whatsapp}</p>
            </div>
          </a>

          <div className={`rounded-2xl border p-5 mt-6 ${dark ? "bg-white/[0.03] border-white/10" : "bg-[#E2E8F0]/30 border-[#E2E8F0]"}`}>
            <p className={`text-xs leading-relaxed ${dark ? "text-white/45" : "text-[#0F172A]/45"}`}>
              💡 <strong>{isEs ? "Tiempo de respuesta habitual:" : "Typical response time:"}</strong>{" "}
              {isEs
                ? "en menos de 24 horas. Nos encantaría conocer los objetivos de tu proyecto y darte un presupuesto gratuito."
                : "within 24 hours. We'd love to hear about your project goals and get you a free quote."}
            </p>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}
